import { createVuetify } from 'vuetify'
import { en } from 'vuetify/locale'

import config from '../configs'
import { DEFAULT_LANGUAGE, RTL_LANGUAGES } from '../utils/constants'

import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import '../assets/scss/theme.scss'

import '@mdi/font/css/materialdesignicons.css'

/**
 * Vuetify Plugin
 * Main components library
 *
 * https://vuetifyjs.com/
 */

// Map our language codes to Vuetify locale import names
// Only English is loaded initially; others are lazy-loaded via loadVuetifyLocale()
// null = no Vuetify locale available for this language
export const VUETIFY_LOCALE_MAP = {
  ar: 'ar',
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  he: 'he',
  hi: null,     // Hindi - no Vuetify locale available
  it: 'it',
  ja: 'ja',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  ru: 'ru',
  sr: 'srLatn', // Serbian Latin → Vuetify uses 'srLatn'
  sv: 'sv',
  uk: 'uk',
  ur: null,     // Urdu - no Vuetify locale available
  zh: 'zhHans'  // Chinese Simplified → Vuetify uses 'zhHans'
}

// Build RTL config from RTL_LANGUAGES constant
const rtl = {}
for (const lang of RTL_LANGUAGES) {
  rtl[lang] = true
}

// Build initial messages with English + stubs for RTL languages without Vuetify translations
// This ensures Vuetify recognizes these locales and applies RTL mode
const initialMessages = { en }
for (const lang of RTL_LANGUAGES) {
  if (!VUETIFY_LOCALE_MAP[lang]) {
    // Add empty stub so Vuetify recognizes this locale (will fallback to 'en' for actual messages)
    initialMessages[lang] = {}
  }
}

// Determine initial Vuetify locale
// Use stored language if Vuetify has translations for it OR if it's an RTL language
// This ensures RTL languages trigger Vuetify's RTL mode even without translations
const storedLang = localStorage.selectedLanguage
const initialVuetifyLocale = (storedLang && (VUETIFY_LOCALE_MAP[storedLang] || RTL_LANGUAGES.includes(storedLang)))
  ? storedLang
  : DEFAULT_LANGUAGE

const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'mdi'
  },
  theme: {
    defaultTheme: 'light',
    themes: config.theme.themes
  },
  locale: {
    locale: initialVuetifyLocale,
    fallback: DEFAULT_LANGUAGE,
    messages: initialMessages,
    rtl
  },
  options: {
    customProperties: true
  }
})

/**
 * Lazy-load a Vuetify locale and add it to vuetify instance
 * Called from i18n-lazy.js when loading app translations
 * @param {string} locale - Our locale code (e.g., 'de', 'he')
 * @returns {Promise<boolean>} True if loaded successfully
 */
export async function loadVuetifyLocale(locale) {
  // English is already loaded
  if (locale === 'en') return true

  const vuetifyKey = VUETIFY_LOCALE_MAP[locale]
  if (!vuetifyKey) {
    console.warn(`No Vuetify locale mapping for: ${locale}`)
    return false
  }

  try {
    let messages
    // Dynamic imports for Vuetify locales (creates separate chunks)
    // Note: Vuetify 3 locale files are at vuetify/lib/locale/*.mjs
    switch (vuetifyKey) {
    case 'ar':
      messages = await import(/* webpackChunkName: "vuetify-ar" */ 'vuetify/lib/locale/ar.mjs')
      break
    case 'de':
      messages = await import(/* webpackChunkName: "vuetify-de" */ 'vuetify/lib/locale/de.mjs')
      break
    case 'es':
      messages = await import(/* webpackChunkName: "vuetify-es" */ 'vuetify/lib/locale/es.mjs')
      break
    case 'fr':
      messages = await import(/* webpackChunkName: "vuetify-fr" */ 'vuetify/lib/locale/fr.mjs')
      break
    case 'he':
      messages = await import(/* webpackChunkName: "vuetify-he" */ 'vuetify/lib/locale/he.mjs')
      break
    case 'it':
      messages = await import(/* webpackChunkName: "vuetify-it" */ 'vuetify/lib/locale/it.mjs')
      break
    case 'ja':
      messages = await import(/* webpackChunkName: "vuetify-ja" */ 'vuetify/lib/locale/ja.mjs')
      break
    case 'nl':
      messages = await import(/* webpackChunkName: "vuetify-nl" */ 'vuetify/lib/locale/nl.mjs')
      break
    case 'pl':
      messages = await import(/* webpackChunkName: "vuetify-pl" */ 'vuetify/lib/locale/pl.mjs')
      break
    case 'pt':
      messages = await import(/* webpackChunkName: "vuetify-pt" */ 'vuetify/lib/locale/pt.mjs')
      break
    case 'ru':
      messages = await import(/* webpackChunkName: "vuetify-ru" */ 'vuetify/lib/locale/ru.mjs')
      break
    case 'srLatn':
      messages = await import(/* webpackChunkName: "vuetify-sr" */ 'vuetify/lib/locale/sr-Latn.mjs')
      break
    case 'sv':
      messages = await import(/* webpackChunkName: "vuetify-sv" */ 'vuetify/lib/locale/sv.mjs')
      break
    case 'uk':
      messages = await import(/* webpackChunkName: "vuetify-uk" */ 'vuetify/lib/locale/uk.mjs')
      break
    case 'zhHans':
      messages = await import(/* webpackChunkName: "vuetify-zh-hans" */ 'vuetify/lib/locale/zh-Hans.mjs')
      break
    default:
      console.warn(`Unknown Vuetify locale: ${vuetifyKey}`)
      return false
    }

    // Add the locale to Vuetify using our locale code (not Vuetify's)
    vuetify.locale.messages.value[locale] = messages.default || messages
    return true
  } catch (error) {
    console.error(`Failed to load Vuetify locale ${locale}:`, error)
    return false
  }
}

export default vuetify
